import React, { useState, useMemo } from "react";
import {
    Card,
    Col,
    Row,
    Statistic,
    Table,
    Tag,
    Progress,
    DatePicker,
    Spin,
    Typography,
    Collapse,
    Empty,
    Tooltip,
    Flex,
    Segmented,
} from "antd";
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    ShoppingCartOutlined,
    CalendarOutlined,
    UserOutlined,
    CarOutlined,
    TagOutlined,
    CreditCardOutlined,
    MailOutlined,
    PrinterOutlined,
    BankOutlined,
    ClockCircleOutlined,
    FireOutlined,
    StarOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { getStats } from "../../service/client";
import { useBranchContext } from "../../contexts/branch-context";
import { colors, spacing, shadows } from "../../styles/theme";
import dayjs from "dayjs";
import {
    ResponsiveContainer,

    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    Area,
    AreaChart,
} from "recharts";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled.div`
  padding: ${spacing.lg};
  min-height: 100%;
  overflow-y: auto;
  background: ${colors.backgroundLight};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.xl};
  flex-wrap: wrap;
  gap: ${spacing.md};
`;

const KPICard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${colors.border};
  box-shadow: none;
  transition: all 0.2s ease;
  height: 100%;

  &:hover {
    box-shadow: ${shadows.cardHover};
    transform: translateY(-2px);
  }

  .ant-statistic-title {
    color: ${colors.textSecondary};
    font-size: 13px;
    margin-bottom: 4px;
  }

  .ant-statistic-content {
    font-size: 24px;
    font-weight: 700;
  }
`;

const SectionCard = styled(Card)`
  border-radius: 12px;
  box-shadow: ${shadows.card};
  margin-bottom: ${spacing.lg};

  .ant-card-head {
    border-bottom: 1px solid ${colors.borderLight};
    min-height: 48px;
  }

  .ant-card-head-title {
    font-size: 16px;
    font-weight: 600;
  }
`;

const HourBar = styled.div<{ $height: number; $max: number }>`
  width: 100%;
  height: ${(props) => (props.$max > 0 ? Math.max((props.$height / props.$max) * 80, 4) : 4)}px;
  background: ${colors.primary};
  border-radius: 4px 4px 0 0;
  transition: height 0.3s ease;
  min-height: 4px;
`;

const HourBarContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  height: 100px;
  padding: 8px 0;
`;

const HourLabel = styled.div`
  font-size: 10px;
  color: ${colors.textSecondary};
  text-align: center;
  width: 100%;
`;

const HourBarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

const PercentageTag = styled(Tag) <{ $positive?: boolean }>`
  font-weight: 600;
  border: none;
  background: ${(props) => (props.$positive ? colors.successBackground : colors.errorBackground)};
  color: ${(props) => (props.$positive ? colors.success : colors.error)};
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 350px;
  padding: 8px 0;

  .recharts-cartesian-grid-horizontal line,
  .recharts-cartesian-grid-vertical line {
    stroke: ${colors.borderLight};
    stroke-opacity: 0.5;
  }

  .recharts-text {
    fill: ${colors.textSecondary};
    font-size: 12px;
  }
`;

const ChartTooltipContainer = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: ${shadows.medium};

  .tooltip-date {
    font-weight: 600;
    font-size: 13px;
    color: ${colors.textPrimary};
    margin-bottom: 8px;
  }

  .tooltip-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    font-size: 13px;
  }

  .tooltip-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .tooltip-label {
    color: ${colors.textSecondary};
  }

  .tooltip-value {
    font-weight: 600;
    color: ${colors.textPrimary};
    margin-left: auto;
  }
`;

// ============================================
// HELPERS
// ============================================

const formatPrice = (priceInCents: number): string => {
    return `${(priceInCents / 100).toFixed(2)} zł`;
};

const WEEKDAY_NAME_KEYS = [
    "statsPage.weekdaySun",
    "statsPage.weekdayMon",
    "statsPage.weekdayTue",
    "statsPage.weekdayWed",
    "statsPage.weekdayThu",
    "statsPage.weekdayFri",
    "statsPage.weekdaySat",
];

const DELIVERY_TYPE_LABEL_KEYS: Record<string, string> = {
    TO_THE_ADDRESS: "statsPage.deliveryTypeToTheAddress",
    SELF_PICKUP: "statsPage.deliveryTypeSelfPickup",
    TO_THE_TABLE: "statsPage.deliveryTypeToTheTable",
    EMAIL: "statsPage.deliveryTypeEmail",
    UNKNOWN: "statsPage.deliveryTypeUnknown",
};

// ============================================
// MAIN COMPONENT
// ============================================

export const Stats: React.FC = () => {
    const { t } = useTranslation();
    const { currentBranch } = useBranchContext();

    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(30, "day"),
        dayjs(),
    ]);

    const [chartGranularity, setChartGranularity] = useState<"day" | "week" | "month">("day");

    const {
        data: stats,
        isLoading,
        isError,
    } = useQuery(
        ["stats", currentBranch?.id, dateRange[0]?.format("YYYY-MM-DD"), dateRange[1]?.format("YYYY-MM-DD")],
        () =>
            getStats({
                branchId: currentBranch?.id,
                dateFrom: dateRange[0]?.format("YYYY-MM-DD"),
                dateTo: dateRange[1]?.format("YYYY-MM-DD"),
            }),
        {
            refetchOnWindowFocus: false,
            staleTime: 60_000,
            cacheTime: 300_000,
        }
    );

    const monthChange = useMemo(() => {
        if (!stats) return 0;
        const prev = stats.revenue.previousMonth;
        const curr = stats.revenue.thisMonth;
        if (prev === 0) return curr > 0 ? 100 : 0;
        return Math.round(((curr - prev) / prev) * 100);
    }, [stats]);

    const chartData = useMemo(() => {
        if (!stats?.revenue?.revenueByDay) return [];

        const rawDays = stats.revenue.revenueByDay as any[];

        if (chartGranularity === "day") {
            return rawDays.map((d: any) => ({
                date: dayjs(d.date).format("DD.MM"),
                fullDate: dayjs(d.date).format("DD.MM.YYYY"),
                total: d.revenue / 100,
                produkty: (d.productRevenue || d.revenue) / 100,
                uslugi: (d.serviceRevenue || 0) / 100,
                zamowienia: d.count,
            }));
        }

        // Aggregate by week or month
        const grouped: Record<string, { total: number; produkty: number; uslugi: number; zamowienia: number; label: string }> = {};

        for (const d of rawDays) {
            const dt = dayjs(d.date);
            let key: string;
            let label: string;

            if (chartGranularity === "week") {
                const weekStart = dt.startOf("week");
                key = weekStart.format("YYYY-MM-DD");
                label = `${weekStart.format("DD.MM")} - ${weekStart.add(6, "day").format("DD.MM")}`;
            } else {
                key = dt.format("YYYY-MM");
                label = dt.format("MMM YYYY");
            }

            if (!grouped[key]) {
                grouped[key] = { total: 0, produkty: 0, uslugi: 0, zamowienia: 0, label };
            }
            grouped[key].total += d.revenue / 100;
            grouped[key].produkty += (d.productRevenue || d.revenue) / 100;
            grouped[key].uslugi += (d.serviceRevenue || 0) / 100;
            grouped[key].zamowienia += d.count;
        }

        return Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([_, data]) => ({
                date: data.label,
                fullDate: data.label,
                total: Math.round(data.total * 100) / 100,
                produkty: Math.round(data.produkty * 100) / 100,
                uslugi: Math.round(data.uslugi * 100) / 100,
                zamowienia: data.zamowienia,
            }));
    }, [stats, chartGranularity]);

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;
        const data = payload[0]?.payload;
        return (
            <ChartTooltipContainer>
                <div className="tooltip-date">{data?.fullDate || label}</div>
                <div className="tooltip-row">
                    <div className="tooltip-dot" style={{ background: '#6366f1' }} />
                    <span className="tooltip-label">{t("statsPage.tooltipTotal")}</span>
                    <span className="tooltip-value">{data?.total?.toFixed(2)} zł</span>
                </div>
                <div className="tooltip-row">
                    <div className="tooltip-dot" style={{ background: '#10b981' }} />
                    <span className="tooltip-label">{t("statsPage.tooltipProducts")}</span>
                    <span className="tooltip-value">{data?.produkty?.toFixed(2)} zł</span>
                </div>
                <div className="tooltip-row">
                    <div className="tooltip-dot" style={{ background: '#f59e0b' }} />
                    <span className="tooltip-label">{t("statsPage.tooltipServices")}</span>
                    <span className="tooltip-value">{data?.uslugi?.toFixed(2)} zł</span>
                </div>
                <div className="tooltip-row">
                    <div className="tooltip-dot" style={{ background: '#94a3b8' }} />
                    <span className="tooltip-label">{t("statsPage.tooltipOrders")}</span>
                    <span className="tooltip-value">{data?.zamowienia}</span>
                </div>
            </ChartTooltipContainer>
        );
    };

    if (isLoading) {
        return (
            <PageContainer>
                <Flex justify="center" align="center" style={{ minHeight: 400 }}>
                    <Spin size="large" tip={t("statsPage.loading")} />
                </Flex>
            </PageContainer>
        );
    }

    if (isError || !stats) {
        return (
            <PageContainer>
                <Empty description={t("statsPage.loadError")} />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {/* HEADER */}
            <PageHeader>
                <Title level={3} style={{ margin: 0 }}>
                    📊 {t("statsPage.pageTitle")}
                </Title>
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                        if (dates && dates[0] && dates[1]) {
                            setDateRange([dates[0], dates[1]]);
                        }
                    }}
                    format="DD.MM.YYYY"
                    allowClear={false}
                />
            </PageHeader>

            {/* ========== 1. REVENUE KPI CARDS ========== */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <KPICard>
                        <Statistic
                            title={t("statsPage.kpiRevenueToday")}
                            value={stats.revenue.today / 100}
                            precision={2}
                            suffix="zł"
                            prefix={<ShoppingCartOutlined />}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {t("statsPage.ordersCount", { count: stats.revenue.todayOrderCount })}
                        </Text>
                    </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard>
                        <Statistic
                            title={t("statsPage.kpiRevenueThisMonth")}
                            value={stats.revenue.thisMonth / 100}
                            precision={2}
                            suffix="zł"
                            prefix={
                                monthChange >= 0 ? (
                                    <ArrowUpOutlined style={{ color: "var(--color-success)" }} />
                                ) : (
                                    <ArrowDownOutlined style={{ color: "var(--color-error)" }} />
                                )
                            }
                        />
                        <PercentageTag $positive={monthChange >= 0}>
                            {monthChange >= 0 ? "+" : ""}
                            {t("statsPage.percentVsPreviousMonth", { value: monthChange })}
                        </PercentageTag>
                    </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard>
                        <Statistic
                            title={t("statsPage.kpiAvgOrderValue")}
                            value={stats.revenue.avgOrderValue / 100}
                            precision={2}
                            suffix="zł"
                            prefix={<StarOutlined />}
                        />
                    </KPICard>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <KPICard>
                        <Statistic
                            title={t("statsPage.kpiNewClientsThisMonth")}
                            value={stats.clients.newThisMonth}
                            prefix={<UserOutlined />}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {t("statsPage.outOfTotal", { total: stats.clients.total })}
                        </Text>
                    </KPICard>
                </Col>
            </Row>

            {/* ========== COLLAPSIBLE SECTIONS ========== */}
            <Collapse
                defaultActiveKey={[]}
                ghost
                style={{ background: "transparent" }}
            >
                {/* ========== 1. PRZYCHODY ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            💰 {t("statsPage.panelRevenue")}
                        </Text>
                    }
                    key="revenue"
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <SectionCard title={t("statsPage.revenueInPeriod")} size="small">
                                <Row gutter={[16, 8]}>
                                    <Col span={12}>
                                        <Statistic title={t("statsPage.products")} value={stats.revenue.totalInRange / 100 - (stats.serviceOrders.totalInRange > 0 ? stats.serviceOrders.avgPrice * stats.serviceOrders.totalInRange / 10000 : 0)} precision={2} suffix="zł" />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title={t("statsPage.thisWeek")} value={stats.revenue.thisWeek / 100} precision={2} suffix="zł" />
                                    </Col>
                                </Row>
                            </SectionCard>
                        </Col>
                        <Col xs={24} md={12}>
                            <SectionCard title={t("statsPage.revenuePerBranch")} size="small">
                                <Table
                                    dataSource={stats.revenue.revenueByBranch}
                                    columns={[
                                        { title: t("statsPage.colBranch"), dataIndex: "branchName", key: "branchName" },
                                        {
                                            title: t("statsPage.colRevenue"),
                                            dataIndex: "revenue",
                                            key: "revenue",
                                            render: (val: number) => formatPrice(val),
                                            sorter: (a: any, b: any) => a.revenue - b.revenue,
                                        },
                                        { title: t("statsPage.colOrders"), dataIndex: "count", key: "count" },
                                    ]}
                                    rowKey="branchId"
                                    pagination={false}
                                    size="small"
                                />
                            </SectionCard>
                        </Col>
                    </Row>

                    {/* Revenue over time chart */}
                    <SectionCard
                        title={`📈 ${t("statsPage.revenueOverTime")}`}
                        size="small"
                        extra={
                            <Flex align="center" gap={12} wrap="wrap">
                                <Segmented
                                    size="small"
                                    value={chartGranularity}
                                    onChange={(val) => setChartGranularity(val as "day" | "week" | "month")}
                                    options={[
                                        { label: t("statsPage.granularityDay"), value: "day" },
                                        { label: t("statsPage.granularityWeek"), value: "week" },
                                        { label: t("statsPage.granularityMonth"), value: "month" },
                                    ]}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {dateRange[0]?.format("DD.MM.YYYY")} – {dateRange[1]?.format("DD.MM.YYYY")}
                                </Text>
                            </Flex>
                        }
                    >
                        {chartData.length > 0 ? (
                            <ChartContainer>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={chartData}
                                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                                    >
                                        <defs>
                                            <linearGradient id="gradientTotal" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="gradientProducts" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="gradientServices" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15} />
                                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 11 }}
                                            tickLine={false}
                                            axisLine={false}
                                            interval={chartData.length > 14 ? Math.floor(chartData.length / 7) : 0}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11 }}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`}
                                            width={50}
                                        />
                                        <RechartsTooltip content={<CustomTooltip />} />
                                        <Legend
                                            iconType="circle"
                                            iconSize={8}
                                            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="total"
                                            name={t("statsPage.chartTotal")}
                                            stroke="#6366f1"
                                            strokeWidth={2.5}
                                            fill="url(#gradientTotal)"
                                            dot={chartData.length <= 31}
                                            activeDot={{ r: 5, strokeWidth: 2 }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="produkty"
                                            name={t("statsPage.chartProducts")}
                                            stroke="#10b981"
                                            strokeWidth={1.5}
                                            fill="url(#gradientProducts)"
                                            dot={false}
                                            strokeDasharray="4 2"
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="uslugi"
                                            name={t("statsPage.chartServices")}
                                            stroke="#f59e0b"
                                            strokeWidth={1.5}
                                            fill="url(#gradientServices)"
                                            dot={false}
                                            strokeDasharray="4 2"
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </ChartContainer>
                        ) : (
                            <Empty description={t("statsPage.noRevenueData")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        )}
                    </SectionCard>

                    {/* Revenue by day table */}
                    <SectionCard title={t("statsPage.revenueDailyTable")} size="small">
                        <Table
                            dataSource={stats.revenue.revenueByDay?.slice(-14)}
                            columns={[
                                { title: t("statsPage.colDate"), dataIndex: "date", key: "date" },
                                {
                                    title: t("statsPage.colRevenue"),
                                    dataIndex: "revenue",
                                    key: "revenue",
                                    render: (val: number) => formatPrice(val),
                                },
                                { title: t("statsPage.colOrders"), dataIndex: "count", key: "count" },
                            ]}
                            rowKey="date"
                            pagination={false}
                            size="small"
                        />
                    </SectionCard>
                </Panel>

                {/* ========== 2. ZAMÓWIENIA PRODUKTOWE ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            📦 {t("statsPage.panelProductOrders")}
                        </Text>
                    }
                    key="productOrders"
                >
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.pending")} value={stats.productOrders.pending} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.inProgress")} value={stats.productOrders.inProgress} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.inDelivery")} value={stats.productOrders.inDelivery} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.doneToday")} value={stats.productOrders.doneToday} />
                            </KPICard>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                            <SectionCard title={t("statsPage.statistics")} size="small">
                                <Row gutter={[8, 12]}>
                                    <Col span={24}>
                                        <Text type="secondary">{t("statsPage.refundRate")}</Text>
                                        <Progress
                                            percent={stats.productOrders.refundRate}
                                            status={stats.productOrders.refundRate > 10 ? "exception" : "normal"}
                                            size="small"
                                        />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title={t("statsPage.online")} value={stats.productOrders.onlineCount} valueStyle={{ fontSize: 18 }} />
                                    </Col>
                                    <Col span={12}>
                                        <Statistic title={t("statsPage.internal")} value={stats.productOrders.internalCount} valueStyle={{ fontSize: 18 }} />
                                    </Col>
                                    <Col span={24}>
                                        <Statistic title={t("statsPage.totalInRange")} value={stats.productOrders.totalInRange} valueStyle={{ fontSize: 18 }} />
                                    </Col>
                                </Row>
                            </SectionCard>
                        </Col>

                        <Col xs={24} md={8}>
                            <SectionCard title={t("statsPage.deliveryMethods")} size="small">
                                <Table
                                    dataSource={stats.productOrders.ordersByDelivery}
                                    columns={[
                                        {
                                            title: t("statsPage.colType"),
                                            dataIndex: "deliveryType",
                                            key: "deliveryType",
                                            render: (deliveryType: string) => DELIVERY_TYPE_LABEL_KEYS[deliveryType] ? t(DELIVERY_TYPE_LABEL_KEYS[deliveryType]) : deliveryType,
                                        },
                                        { title: t("statsPage.colQuantity"), dataIndex: "count", key: "count" },
                                        {
                                            title: t("statsPage.colRevenue"),
                                            dataIndex: "revenue",
                                            key: "revenue",
                                            render: (val: number) => formatPrice(val),
                                        },
                                    ]}
                                    rowKey="deliveryMethodId"
                                    pagination={false}
                                    size="small"
                                />
                            </SectionCard>
                        </Col>

                        <Col xs={24} md={8}>
                            <SectionCard title={t("statsPage.peakHours")} size="small">
                                <HourBarContainer>
                                    {stats.productOrders.ordersByHour?.map((count: number, hour: number) => {
                                        const max = Math.max(...(stats.productOrders.ordersByHour || [1]));
                                        return (
                                            <HourBarWrapper key={hour}>
                                                <Tooltip title={t("statsPage.hourOrdersTooltip", { hour, count })}>
                                                    <HourBar $height={count} $max={max} />
                                                </Tooltip>
                                                {hour % 3 === 0 && <HourLabel>{hour}</HourLabel>}
                                            </HourBarWrapper>
                                        );
                                    })}
                                </HourBarContainer>
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {t("statsPage.ordersByWeekday")}{" "}
                                        {stats.productOrders.ordersByWeekday?.map((count: number, day: number) => (
                                            <Tag key={day} style={{ margin: 2 }}>
                                                {t(WEEKDAY_NAME_KEYS[day])}: {count}
                                            </Tag>
                                        ))}
                                    </Text>
                                </div>
                            </SectionCard>
                        </Col>
                    </Row>
                </Panel>

                {/* ========== 3. REZERWACJE USŁUG ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            📅 {t("statsPage.panelServiceOrders")}
                        </Text>
                    }
                    key="serviceOrders"
                >
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.today")} value={stats.serviceOrders.today} prefix={<CalendarOutlined />} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.pendingAcceptance")} value={stats.serviceOrders.pendingAcceptance} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.acceptanceRate")}
                                    value={stats.serviceOrders.acceptanceRate}
                                    suffix="%"
                                />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.rejectionRate")}
                                    value={stats.serviceOrders.rejectionRate}
                                    suffix="%"
                                />
                            </KPICard>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={8}>
                            <SectionCard title={t("statsPage.additionalMetrics")} size="small">
                                <Statistic
                                    title={t("statsPage.avgReservationPrice")}
                                    value={stats.serviceOrders.avgPrice / 100}
                                    precision={2}
                                    suffix="zł"
                                    style={{ marginBottom: 12 }}
                                />
                                <Text type="secondary">{t("statsPage.serviceRefundRate")}</Text>
                                <Progress percent={stats.serviceOrders.refundRate} size="small" />
                            </SectionCard>
                        </Col>
                        <Col xs={24} md={16}>
                            <SectionCard title={t("statsPage.serviceRanking")} size="small">
                                <Table
                                    dataSource={stats.serviceOrders.byService}
                                    columns={[
                                        { title: t("statsPage.colService"), dataIndex: "serviceName", key: "serviceName" },
                                        {
                                            title: t("statsPage.colReservations"),
                                            dataIndex: "count",
                                            key: "count",
                                            sorter: (a: any, b: any) => a.count - b.count,
                                            defaultSortOrder: "descend" as const,
                                        },
                                        {
                                            title: t("statsPage.colRevenue"),
                                            dataIndex: "revenue",
                                            key: "revenue",
                                            render: (val: number) => formatPrice(val),
                                        },
                                    ]}
                                    rowKey="serviceId"
                                    pagination={false}
                                    size="small"
                                />
                            </SectionCard>
                        </Col>
                    </Row>
                </Panel>

                {/* ========== 4. POPULARNOŚĆ PRODUKTÓW ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            🍕 {t("statsPage.panelPopularProducts")}
                        </Text>
                    }
                    key="popularProducts"
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={14}>
                            <SectionCard
                                title={t("statsPage.top10Products")}
                                extra={<FireOutlined style={{ color: "var(--color-error)" }} />}
                                size="small"
                            >
                                <Table
                                    dataSource={stats.popularProducts.top10}
                                    columns={[
                                        {
                                            title: "#",
                                            render: (_: any, __: any, index: number) => (
                                                <Tag color={index < 3 ? "gold" : "default"}>{index + 1}</Tag>
                                            ),
                                            width: 50,
                                        },
                                        { title: t("statsPage.colProduct"), dataIndex: "name", key: "name" },
                                        { title: t("statsPage.colQuantity"), dataIndex: "count", key: "count" },
                                        {
                                            title: t("statsPage.colRevenue"),
                                            dataIndex: "revenue",
                                            key: "revenue",
                                            render: (val: number) => formatPrice(val),
                                        },
                                    ]}
                                    rowKey="name"
                                    pagination={false}
                                    size="small"
                                />
                            </SectionCard>
                        </Col>
                        <Col xs={24} md={10}>
                            <SectionCard title={t("statsPage.neverOrderedProducts")} size="small">
                                {stats.popularProducts.neverOrdered?.length > 0 ? (
                                    <Table
                                        dataSource={stats.popularProducts.neverOrdered}
                                        columns={[
                                            { title: t("statsPage.colId"), dataIndex: "id", key: "id", width: 50 },
                                            { title: t("statsPage.colName"), dataIndex: "name", key: "name" },
                                        ]}
                                        rowKey="id"
                                        pagination={false}
                                        size="small"
                                    />
                                ) : (
                                    <Empty description={t("statsPage.allProductsHaveOrders")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                )}
                            </SectionCard>
                        </Col>
                    </Row>
                </Panel>

                {/* ========== 5. KLIENCI ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            👥 {t("statsPage.panelClients")}
                        </Text>
                    }
                    key="clients"
                >
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.total")} value={stats.clients.total} prefix={<UserOutlined />} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.newThisMonth")} value={stats.clients.newThisMonth} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.returning")}
                                    value={stats.clients.returning}
                                    suffix={
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            ({stats.clients.retentionRate}%)
                                        </Text>
                                    }
                                />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.marketingConsent")}
                                    value={stats.clients.marketingConsent}
                                    suffix={
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            ({stats.clients.marketingConsentRate}%)
                                        </Text>
                                    }
                                />
                            </KPICard>
                        </Col>
                    </Row>

                    <SectionCard title={t("statsPage.topClients")} size="small">
                        <Table
                            dataSource={stats.clients.topClients}
                            columns={[
                                {
                                    title: "#",
                                    render: (_: any, __: any, index: number) => index + 1,
                                    width: 50,
                                },
                                { title: t("statsPage.colClient"), dataIndex: "name", key: "name" },
                                { title: t("statsPage.colOrders"), dataIndex: "ordersCount", key: "ordersCount" },
                            ]}
                            rowKey="id"
                            pagination={false}
                            size="small"
                        />
                    </SectionCard>
                </Panel>

                {/* ========== 6. DOSTAWY ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            🚚 {t("statsPage.panelDelivery")}
                        </Text>
                    }
                    key="delivery"
                >
                    <SectionCard title={t("statsPage.deliveryMethodsBreakdown")} size="small" extra={<CarOutlined />}>
                        <Table
                            dataSource={stats.delivery.ordersByDelivery}
                            columns={[
                                { title: t("statsPage.colMethod"), dataIndex: "deliveryName", key: "deliveryName" },
                                {
                                    title: t("statsPage.colType"),
                                    dataIndex: "deliveryType",
                                    key: "deliveryType",
                                    render: (deliveryType: string) => (
                                        <Tag>{DELIVERY_TYPE_LABEL_KEYS[deliveryType] ? t(DELIVERY_TYPE_LABEL_KEYS[deliveryType]) : deliveryType}</Tag>
                                    ),
                                },
                                { title: t("statsPage.colOrders"), dataIndex: "count", key: "count" },
                                {
                                    title: t("statsPage.colRevenue"),
                                    dataIndex: "revenue",
                                    key: "revenue",
                                    render: (val: number) => formatPrice(val),
                                },
                            ]}
                            rowKey="deliveryMethodId"
                            pagination={false}
                            size="small"
                        />
                    </SectionCard>
                </Panel>

                {/* ========== 7. KUPONY ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            🎟️ {t("statsPage.panelCoupons")}
                        </Text>
                    }
                    key="coupons"
                >
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.activeCoupons")} value={stats.coupons.active} prefix={<TagOutlined />} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.ordersWithCoupon")} value={stats.coupons.ordersWithCoupon} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.aovWithCoupon")}
                                    value={stats.coupons.avgOrderWithCoupon / 100}
                                    precision={2}
                                    suffix="zł"
                                />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.aovWithoutCoupon")}
                                    value={stats.coupons.avgOrderWithoutCoupon / 100}
                                    precision={2}
                                    suffix="zł"
                                />
                            </KPICard>
                        </Col>
                    </Row>

                    <SectionCard title={t("statsPage.couponList")} size="small">
                        <Table
                            dataSource={stats.coupons.list}
                            columns={[
                                { title: t("statsPage.colCode"), dataIndex: "code", key: "code", render: (code: string) => <Tag color="blue">{code}</Tag> },
                                {
                                    title: t("statsPage.colType"),
                                    dataIndex: "type",
                                    key: "type",
                                    render: (type: string) => (type === "PERCENTAGE" ? "%" : "zł"),
                                },
                                {
                                    title: t("statsPage.colValue"),
                                    dataIndex: "value",
                                    key: "value",
                                    render: (val: number, record: any) =>
                                        record.type === "PERCENTAGE" ? `${val}%` : formatPrice(val),
                                },
                                {
                                    title: t("statsPage.colUsage"),
                                    key: "usage",
                                    render: (_: any, record: any) => (
                                        <div>
                                            <Progress
                                                percent={Math.round(
                                                    (record.currentUsageCount / Math.max(record.maxUsageCount, 1)) * 100
                                                )}
                                                size="small"
                                                format={() => `${record.currentUsageCount}/${record.maxUsageCount}`}
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    title: t("statsPage.colStatus"),
                                    dataIndex: "isActive",
                                    key: "isActive",
                                    render: (active: boolean) => (
                                        <Tag color={active ? "green" : "red"}>
                                            {active ? t("statsPage.statusActive") : t("statsPage.statusInactive")}
                                        </Tag>
                                    ),
                                },
                            ]}
                            rowKey="id"
                            pagination={false}
                            size="small"
                        />
                    </SectionCard>
                </Panel>

                {/* ========== 8. PŁATNOŚCI ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            💳 {t("statsPage.panelPayments")}
                        </Text>
                    }
                    key="payments"
                >
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={24} sm={8}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.paymentSuccessRate")}
                                    value={stats.payments.successRate}
                                    suffix="%"
                                    prefix={<CreditCardOutlined />}
                                />
                            </KPICard>
                        </Col>
                        <Col xs={24} sm={8}>
                            <KPICard>
                                <Statistic title={t("statsPage.abandonedPayments")} value={stats.payments.abandoned} />
                            </KPICard>
                        </Col>
                        <Col xs={24} sm={8}>
                            <SectionCard title={t("statsPage.paymentsPerType")} size="small">
                                {Object.entries(stats.payments.byType || {}).map(([type, data]: [string, any]) => (
                                    <div key={type} style={{ marginBottom: 8 }}>
                                        <Flex justify="space-between">
                                            <Tag>{type}</Tag>
                                            <Text>
                                                {t("statsPage.transactionsRevenue", { count: data.count, price: formatPrice(data.revenue) })}
                                            </Text>
                                        </Flex>
                                    </div>
                                ))}
                            </SectionCard>
                        </Col>
                    </Row>
                </Panel>

                {/* ========== 9. MARKETING ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            📱 {t("statsPage.panelMarketing")}
                        </Text>
                    }
                    key="marketing"
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.smsCampaigns")} value={stats.marketing.smsCampaignsSent} prefix={<MailOutlined />} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.emailCampaigns")} value={stats.marketing.emailCampaignsSent} />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.smsCost")}
                                    value={stats.marketing.smsCost / 100}
                                    precision={2}
                                    suffix="zł"
                                />
                            </KPICard>
                        </Col>
                        <Col xs={12} sm={6}>
                            <KPICard>
                                <Statistic title={t("statsPage.smsLeft")} value={stats.marketing.smsLeft} />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {t("statsPage.marketingBase", { base: stats.marketing.marketingBase })}
                                </Text>
                            </KPICard>
                        </Col>
                    </Row>
                </Panel>

                {/* ========== 10. DRUKARKA ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            🖨️ {t("statsPage.panelPrinter")}
                        </Text>
                    }
                    key="printer"
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.printedToday")}
                                    value={stats.printer.printedToday}
                                    prefix={<PrinterOutlined />}
                                />
                            </KPICard>
                        </Col>
                        <Col xs={24} sm={8}>
                            <KPICard>
                                <Statistic title={t("statsPage.notPrinted")} value={stats.printer.notPrinted} />
                            </KPICard>
                        </Col>
                        <Col xs={24} sm={8}>
                            <KPICard>
                                <Statistic title={t("statsPage.printRate")} value={stats.printer.printRate} suffix="%" />
                                <Progress percent={stats.printer.printRate} showInfo={false} size="small" />
                            </KPICard>
                        </Col>
                    </Row>
                </Panel>

                {/* ========== 11. PORÓWNANIA ODDZIAŁÓW ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            📊 {t("statsPage.panelBranches")}
                        </Text>
                    }
                    key="branches"
                >
                    <SectionCard title={t("statsPage.branchRanking")} size="small" extra={<BankOutlined />}>
                        <Table
                            dataSource={stats.branches.ranking}
                            columns={[
                                {
                                    title: "#",
                                    render: (_: any, __: any, index: number) => (
                                        <Tag color={index < 3 ? "gold" : "default"}>{index + 1}</Tag>
                                    ),
                                    width: 50,
                                },
                                { title: t("statsPage.colBranch"), dataIndex: "branchName", key: "branchName" },
                                {
                                    title: t("statsPage.colProductRevenue"),
                                    dataIndex: "productRevenue",
                                    key: "productRevenue",
                                    render: (val: number) => formatPrice(val),
                                },
                                { title: t("statsPage.colProductOrders"), dataIndex: "productCount", key: "productCount" },
                                {
                                    title: t("statsPage.colServiceRevenue"),
                                    dataIndex: "serviceRevenue",
                                    key: "serviceRevenue",
                                    render: (val: number) => formatPrice(val),
                                },
                                { title: t("statsPage.colReservations"), dataIndex: "serviceCount", key: "serviceCount" },
                                {
                                    title: t("statsPage.colTotal"),
                                    dataIndex: "totalRevenue",
                                    key: "totalRevenue",
                                    render: (val: number) => <Text strong>{formatPrice(val)}</Text>,
                                    sorter: (a: any, b: any) => a.totalRevenue - b.totalRevenue,
                                    defaultSortOrder: "descend" as const,
                                },
                            ]}
                            rowKey="branchId"
                            pagination={false}
                            size="small"
                        />
                    </SectionCard>
                </Panel>

                {/* ========== 12. OPERACYJNE ========== */}
                <Panel
                    header={
                        <Text strong style={{ fontSize: 16 }}>
                            ⏰ {t("statsPage.panelOperational")}
                        </Text>
                    }
                    key="operational"
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8}>
                            <KPICard>
                                <Statistic
                                    title={t("statsPage.avgFulfillmentTime")}
                                    value={stats.operational.avgFulfillmentMinutes}
                                    suffix="min"
                                    prefix={<ClockCircleOutlined />}
                                />
                            </KPICard>
                        </Col>
                        <Col xs={24} sm={16}>
                            <SectionCard title={t("statsPage.ordersPerHour")} size="small">
                                <HourBarContainer>
                                    {stats.operational.ordersByHour?.map((count: number, hour: number) => {
                                        const max = Math.max(...(stats.operational.ordersByHour || [1]));
                                        return (
                                            <HourBarWrapper key={hour}>
                                                <Tooltip title={t("statsPage.hourOrdersTooltip", { hour, count })}>
                                                    <HourBar $height={count} $max={max} />
                                                </Tooltip>
                                                {hour % 3 === 0 && <HourLabel>{hour}</HourLabel>}
                                            </HourBarWrapper>
                                        );
                                    })}
                                </HourBarContainer>
                            </SectionCard>
                        </Col>
                    </Row>
                </Panel>
            </Collapse>
        </PageContainer>
    );
};
